import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Lock, ArrowRight, KeyRound, CheckCircle2, X, Mail } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { adminApi, setToken, formatApiErrorDetail } from "@/lib/adminApi";
import { navigate } from "@/lib/nav";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password modal state
  const [showForgot, setShowForgot] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: send code, 2: verify & set pass
  const [resetEmail, setResetEmail] = useState("info@suryavanshexim.com");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi.login(email, password);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const res = await adminApi.sendResetCode(resetEmail);
      toast.success(`6-Digit security reset code sent to info@suryavanshexim.com!`);
      setResetStep(2);
    } catch (err) {
      toast.error("Failed to send reset code. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await adminApi.resetPassword(resetCode, newPassword);
      toast.success("Password reset successfully! Log in with your new password.");
      setShowForgot(false);
      setPassword(newPassword);
      setResetStep(1);
      setResetCode("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.message || "Invalid reset code. Check info@suryavanshexim.com");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-[#0B1320] px-5">
      <div className="pointer-events-none absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 30% 20%, rgba(230,81,0,0.25), transparent 45%)" }} />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass relative z-10 w-full max-w-md rounded-3xl p-8 sm:p-10"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="h-16 w-16" />
          <span className="data-label mt-4">Admin Portal</span>
          <h1 className="mt-1 font-display text-2xl font-bold uppercase text-white">Suryavansh Exim</h1>
          <p className="mt-1 text-sm text-slate-400">Enter your credentials to continue</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="data-label mb-1.5 block">Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@suryavanshexim.com"
              className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E65100]"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="data-label block">Password</label>
              <button
                type="button"
                onClick={() => {
                  setShowForgot(true);
                  setResetStep(1);
                }}
                className="text-xs text-[#D4AF37] hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            <input
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E65100]"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#E65100] px-6 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-[#FF6D00] hover:shadow-[0_0_30px_rgba(230,81,0,0.5)] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing in..." : "Sign In"}
            {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
          </button>
        </form>

        <a href="/" className="mt-6 block text-center text-xs text-slate-500 transition-colors hover:text-[#D4AF37]">← Back to website</a>
      </motion.div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowForgot(false)} />
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="glass relative z-10 w-full max-w-md rounded-3xl p-6 sm:p-8"
            >
              <button
                onClick={() => setShowForgot(false)}
                className="absolute right-5 top-5 rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E65100]/20 text-[#FF6D00]">
                  <KeyRound className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-white">Reset Admin Password</h3>
                  <p className="text-xs text-slate-400">Security code sent to info@suryavanshexim.com</p>
                </div>
              </div>

              {resetStep === 1 ? (
                <form onSubmit={handleSendResetCode} className="space-y-4">
                  <div>
                    <label className="data-label mb-1.5 block">Admin Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="info@suryavanshexim.com"
                        className="w-full rounded-xl border border-white/10 bg-[#0B1320] py-3 pl-10 pr-4 text-sm text-white outline-none focus:border-[#E65100]"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E65100] px-6 py-3 font-semibold text-white transition-all hover:bg-[#FF6D00] disabled:opacity-60"
                  >
                    {resetLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send Reset Code to Email"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
                  <div>
                    <label className="data-label mb-1.5 block">6-Digit Security Reset Code</label>
                    <input
                      type="text"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      placeholder="e.g. 784920"
                      className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-center font-mono text-lg tracking-widest text-[#D4AF37] outline-none focus:border-[#E65100]"
                      required
                    />
                    <p className="mt-1 text-[0.7rem] text-slate-400">Check your <span className="text-white">info@suryavanshexim.com</span> inbox for the code</p>
                  </div>
                  <div>
                    <label className="data-label mb-1.5 block">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none focus:border-[#E65100]"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E65100] px-6 py-3 font-semibold text-white transition-all hover:bg-[#FF6D00] disabled:opacity-60"
                  >
                    {resetLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                    {resetLoading ? "Updating Password..." : "Set New Password"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetStep(1)}
                    className="w-full text-center text-xs text-slate-400 hover:text-white"
                  >
                    ← Resend Code
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
